"use client";

import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  PropsWithChildren,
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback
} from "react";
import posthog from "posthog-js";
import { Capacitor } from "@capacitor/core";
import { Hoofdstuk, LoginErrors, WisWizModus } from "@/lib/types";
import { WisWizAPI } from "@/services/api";
import { SubscriptionPlan } from "@/services/api_types";
import { FRONTEND_SUBSCRIPTION_PLANS } from "@/lib/subscriptionPlans";

export type ModusSelectionSource =
  | "mode_card"
  | "quickstart"
  | "expanded_new"
  | "expanded_resume"
  | "auto_restore";

import { NativeBookmark } from "@/hooks/useNativeBookmark";
import useDeepLink from "@/hooks/useDeepLink";
import useBetalingTerug from "@/hooks/useBetalingTerug";
import { getUnzoomedWidth } from "@/lib/viewportUtils";

type AppContext = {
  api: WisWizAPI;

  isMobile: boolean | null;

  // Drawer
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;

  // Mobile development
  devLoginError: LoginErrors | undefined;

  // Profiel selector etc
  hoofdstukken: Hoofdstuk[];
  setHoofdstukken: Dispatch<SetStateAction<Hoofdstuk[]>>;

  currentModus: WisWizModus | undefined;
  setCurrentModus: Dispatch<SetStateAction<WisWizModus | undefined>>;
  selectModus: (modus: WisWizModus, source: ModusSelectionSource) => void;

  nativeBookmark?: NativeBookmark;
  setNativeBookmark: Dispatch<SetStateAction<NativeBookmark | undefined>>;

  // Hide bottom bar (for stappenplan/zelfuitwerken modes on iPad)
  hideBottomBar: boolean;
  setHideBottomBar: Dispatch<SetStateAction<boolean>>;

  // Subscription plans
  subscriptionPlans: SubscriptionPlan[];
};

const AppContext = createContext<AppContext | null>(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === null) {
    throw Error("No AppContext available");
  }
  return context;
};

export const AppContextProvider = ({ children }: PropsWithChildren) => {
  const [hoofdstukken, setHoofdstukken] = useState<Hoofdstuk[]>([]);

  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [devLoginError, setDevLoginError] = useState<LoginErrors>();

  const [currentModus, setCurrentModus] = useState<WisWizModus | undefined>(
    undefined
  );

  const [nativeBookmark, setNativeBookmark] = useState<NativeBookmark>();
  const [hideBottomBar, setHideBottomBar] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>(
    FRONTEND_SUBSCRIPTION_PLANS
  );

  // Don't clear state on mode switch — state persists in memory.
  // isHuiswerkActive gates on currentModus === "huiswerk".
  const selectModus = useCallback(
    (modus: WisWizModus, source: ModusSelectionSource) => {
      setCurrentModus(modus);
      posthog.capture("study_mode_selected", { modus, source });
    },
    [setCurrentModus]
  );

  const openDrawer = () => {
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => {
    // Ensure scroll is re-enabled immediately when closing the drawer
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(getUnzoomedWidth() < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  // Track the visible viewport's bottom inset (= distance from layout
  // viewport bottom to visible viewport bottom = on-screen keyboard +
  // any browser chrome below the visible band) as a CSS var so that
  // sticky-bottom UI can stay above the keyboard. iOS 26 Safari overlays
  // the keyboard rather than resizing the layout viewport, so a plain
  // `position: sticky; bottom: 0` would pin behind the keyboard. The
  // chat input footer instead uses `bottom: var(--vv-bottom-offset)`.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const offset = Math.max(0, window.innerHeight - vv.offsetTop - vv.height);
      document.documentElement.style.setProperty(
        "--vv-bottom-offset",
        `${offset}px`
      );
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  useEffect(() => {
    if (isMobile === null) return;
    // Refine `platform_label` (initially set via UA heuristic in
    // PostHogProvider) to the authoritative value once useLayoutMode has
    // resolved. Dashboards break down on platform_label for the four-way
    // web_desktop / web_mobile / native_ios / native_android split.
    const platformLabel = Capacitor.isNativePlatform()
      ? `native_${Capacitor.getPlatform()}`
      : isMobile
        ? "web_mobile"
        : "web_desktop";
    posthog.register({
      is_mobile_layout: isMobile,
      platform_label: platformLabel
    });
  }, [isMobile]);

  const apiInstance = useMemo(() => {
    return new WisWizAPI();
  }, []);

  const apiRef = useRef<WisWizAPI>(apiInstance);

  useEffect(() => {
    apiRef.current.getPlans().then(setSubscriptionPlans).catch(() => {});
  }, []);

  useEffect(() => {
    // Remove deprecated local storage item on load
    // TODO: remove after 2026-04-01 (cleanup deprecated localStorage keys)
    try {
      window.localStorage.removeItem("/marker");
      window.localStorage.removeItem("hoofdstuk");
      window.localStorage.removeItem("wiswiz_modus");
    } catch {
      // ignore
    }
  }, []);

  useDeepLink();
  /* wiswiz #156: terug in de app zonder resultaatpagina na een Mollie-hop → status vragen, resultaat tonen */
  useBetalingTerug(apiRef.current);

  const providerValue = useMemo(() => {
    return {
      api: apiRef.current,
      isMobile,
      isDrawerOpen,
      openDrawer,
      closeDrawer,

      devLoginError,

      hoofdstukken,
      setHoofdstukken,

      currentModus,
      setCurrentModus,
      selectModus,
      nativeBookmark,
      setNativeBookmark,
      hideBottomBar,
      setHideBottomBar,
      subscriptionPlans
    };
  }, [
    isMobile,
    isDrawerOpen,

    devLoginError,

    hoofdstukken,
    currentModus,
    nativeBookmark,
    hideBottomBar,
    subscriptionPlans
  ]);

  return (
    <AppContext.Provider value={providerValue}>{children}</AppContext.Provider>
  );
};
